import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { selectStyles, selectStylesMulti } from '../selectStyles';
import { IconMic } from '../icons';
import { normalizeQuery } from './queryOps';
import { featuresOf, plural } from './util';
import './search.css';

const styles = { ...selectStyles, menu: (base, state) => ({ ...selectStyles.menu(base, state), zIndex: 1000 }), clearIndicator: selectStylesMulti.clearIndicator };

const byCount = (rows) =>
  (Array.isArray(rows) ? rows.slice() : []).sort((a, b) => (Number(b.n) || 0) - (Number(a.n) || 0) || String(a.label).localeCompare(String(b.label)));

const placeLabel = (o, { context }) =>
  context === 'menu' ? (
    <div className="ss-opt">
      <span className="ss-opt-main">
        <span className="ss-opt-name">{o.name || o.label}</span>
        {o.name && (o.location || o.city) && <span className="ss-opt-via">{o.location || o.city}</span>}
      </span>
      {typeof o.n === 'number' && <span className="ss-opt-plays">{plural(o.n, 'show')}</span>}
    </div>
  ) : (
    o.name || o.label
  );

export const Toggle = ({ on, label, hint, icon, onClick }) => (
  <button type="button" className={'ss-toggle' + (on ? ' on' : '')} aria-pressed={!!on} title={hint} onClick={onClick}>
    {icon}
    <span>{label}</span>
  </button>
);

// Year range: rc-slider Range over meta.years, era presets from meta.eras.
// Dragging commits on release; the full span means "no year filter".
function YearsField({ meta, q, set }) {
  const span = Array.isArray(meta && meta.years) ? meta.years : null;
  const eras = Array.isArray(meta && meta.eras) ? meta.eras.filter((e) => e && e.key) : [];
  const [drag, setDrag] = useState(null);
  if (!span || !(span[1] > span[0])) return null;
  const [lo, hi] = span;
  const clamp = (y, d) => (Number.isFinite(y) ? Math.min(hi, Math.max(lo, y)) : d);
  const cur = drag || (q.years ? [clamp(q.years[0], lo), clamp(q.years[1], hi)] : [lo, hi]);
  const commit = (v) => {
    setDrag(null);
    const a = Math.min(v[0], v[1]);
    const b = Math.max(v[0], v[1]);
    if (a <= lo && b >= hi) set({ years: null, era: null });
    else set({ years: [a, b], era: null });
  };
  const active = !!(q.years || q.era);
  const shown = cur[0] === cur[1] ? String(cur[0]) : cur[0] + '-' + cur[1];
  return (
    <div className="ss-field ss-years">
      <div className="ss-field-head">
        <span className="ss-label" id="ss-years-label">
          Years
        </span>
        <span className={'ss-years-value' + (active || drag ? ' mono' : '')}>{active || drag ? shown : 'All years'}</span>
        {active && (
          <button type="button" className="ss-textbtn" onClick={() => set({ years: null, era: null })}>
            All years
          </button>
        )}
      </div>
      <div className={'ss-range' + (active || drag ? '' : ' all')}>
        <Range
          min={lo}
          max={hi}
          value={cur}
          allowCross={false}
          onChange={(v) => setDrag(v)}
          onAfterChange={commit}
          ariaLabelGroupForHandles={['From year', 'To year']}
        />
        <div className="ss-range-ends mono" aria-hidden="true">
          <span>{lo}</span>
          <span>{hi}</span>
        </div>
      </div>
      {eras.length > 0 && (
        <div className="ss-chiprow" role="group" aria-labelledby="ss-years-label">
          {eras.map((e) => {
            const on = q.era === e.key;
            return (
              <Toggle
                key={e.key}
                on={on}
                label={e.label}
                hint={e.from && e.to ? e.from + '-' + e.to : e.label}
                onClick={() => (on ? set({ era: null, years: null }) : set({ era: e.key, years: [e.from, e.to] }))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// Venue, city, years (with era presets), soundboard and named vibes.
// Soundboard shows only with features.sbd, vibes only with features.vibes.
export default function FilterControls({ meta, catalog, value, onChange }) {
  const q = normalizeQuery(value);
  const f = featuresOf(meta);
  const cat = catalog || {};
  const venues = useMemo(() => byCount(cat.venues), [cat.venues]);
  const cities = useMemo(() => byCount(cat.cities), [cat.cities]);
  const set = (patch) => onChange && onChange({ ...q, ...patch });

  const venueValue = q.venue ? venues.find((v) => v.id === q.venue.id) || { value: q.venue.id, id: q.venue.id, name: q.venue.name, label: q.venue.name } : null;
  const cityValue = q.city ? cities.find((c) => c.key === q.city.key) || { value: q.city.key, key: q.city.key, label: q.city.label } : null;

  return (
    <div className="ss-filters">
      <div className="ss-field">
        <label className="ss-label" htmlFor="ss-venue">
          Venue
        </label>
        <Select
          inputId="ss-venue"
          classNamePrefix="ss-rs"
          isClearable
          options={venues}
          value={venueValue}
          onChange={(o) => set({ venue: o ? { id: o.id, name: o.name } : null })}
          formatOptionLabel={placeLabel}
          styles={styles}
          placeholder="Any venue"
          noOptionsMessage={() => 'No venue by that name.'}
          maxMenuHeight={260}
        />
      </div>
      <div className="ss-field">
        <label className="ss-label" htmlFor="ss-city">
          City
        </label>
        <Select
          inputId="ss-city"
          classNamePrefix="ss-rs"
          isClearable
          options={cities}
          value={cityValue}
          onChange={(o) => set({ city: o ? { key: o.key, label: o.label } : null })}
          formatOptionLabel={placeLabel}
          styles={styles}
          placeholder="Any city"
          noOptionsMessage={() => 'No city by that name.'}
          maxMenuHeight={260}
        />
      </div>
      <YearsField meta={meta} q={q} set={set} />
      {(f.sbd || f.vibes.length > 0) && (
        <div className="ss-field">
          <span className="ss-label">Sound and vibe</span>
          <div className="ss-chiprow">
            {f.sbd && (
              <Toggle
                on={q.sbd}
                label="Soundboard only"
                hint="The best tape is a soundboard or matrix recording"
                icon={<IconMic size={13} />}
                onClick={() => set({ sbd: !q.sbd })}
              />
            )}
            {f.vibes.map((v) => (
              <Toggle
                key={v.key}
                on={q.vibe === v.key}
                label={v.label || v.key}
                hint={'Shows that score high for ' + (v.label || v.key).toLowerCase()}
                onClick={() => set({ vibe: q.vibe === v.key ? null : v.key })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
